import {
  Collapsible,
  Flex,
  FlexColumn,
  formatNumber,
} from '@equifinance/shared';
import moment from 'moment';
import styled, { css } from 'styled-components';
import { DateFormatStringEnum, formatDateTime } from '@equifinance/shared/utils';
import { AvmResult, ConfidenceBandType, AvmResultType as AvmResultTypeEnum } from '../../../../../../../contexts/checklist';
import { PropertyType } from '../../../../../../../contexts';

const AvmResultHeaderCss = css`${({ theme }) => `
  background: ${theme.colours.neutral[8]};
  border-radius: unset;
  padding: 0;
  padding-right: ${theme.padding[3]};

  .mi-chevron-down, .mi-chevron-up {
    margin-left: 0;
  }
`}`;

const AvmResultHeader = styled(Flex)`${({ theme }) => `
  padding: ${theme.padding[2]} ${theme.padding[6]};
  background-color: ${theme.colours.primary[2]};
  color: ${theme.colours.neutral[10]};
  gap: ${theme.padding[2]};
  font-weight: ${theme.fontWeight.bold}; 
  font-size: ${theme.fontSize[1]};
  align-items: center;
`}`;

const AvmResultType = styled.span``;

const Seperator = styled.span`${({ theme }) => `
  border-right: 1px solid ${theme.colours.neutral[10]};
  height: ${theme.fontSize[1]};
`}`;

const AvmResultDate = styled.span`${({ theme }) => `
  span {
    font-weight: ${theme.fontWeight.normal};
    padding-left: ${theme.padding[2]};
  }
`}`;

const bodyContentCss = css`${({ theme }) => `
  width: auto;
  padding: 0 ${theme.padding[6]};
`}`;

const bodyCss = css`${({ theme }) => `
  background-color: ${theme.colours.primary[8]};
`}`;

const Label = styled.p` ${({ theme }) => ` 
  color:${theme.colours.neutral[2]};
  font-size: ${theme.fontSize[2]};
  line-height: ${theme.fontSize[7]};
`}`;

const Value = styled(Label)` 
  text-align: right;
`;

const Pairs = styled(Flex)` ${({ theme }) => ` 
  align-items:center;
  background:${theme.colours.neutral[10]};
  border-radius:${theme.borderRadius[1]};
  justify-content:space-between;
  box-sizing: border-box;
  padding: ${theme.padding[1]} ${theme.padding[6]};
`}`;

const DataColumn = styled(FlexColumn)`${({ theme }) => `
  flex: 1;
  gap: ${theme.padding[4]};
`}`;

const AvmResultContainer = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[5]};
`}`;

const NoResults = styled.div` ${({ theme }) => `
  flex: 1;
  border-radius:${theme.borderRadius[1]};
  color: ${theme.colours.neutral[2]};
  font-size: ${theme.fontSize[2]};
  padding: ${theme.padding[2]} ${theme.padding[6]};
  background:${theme.colours.neutral[10]};
`}`;

const OutputHeader = styled(Pairs)` ${({ theme }) => ` 
  background: ${theme.colours.primary[7]};
  color: ${theme.colours.primary[1]};
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.bold};
  padding: ${theme.padding[2]} ${theme.padding[6]};
`}`;

function Output({ label, value }: { label: string, value: any }) {
  return (
    <>
      {value != null && (
        <Pairs>
          <Label>{label}</Label>
          <Value>{value}</Value>
        </Pairs>
      )}
    </>
  );
}

const formatDateValue = (value: string) => {
  if (!value || !moment(value).isValid()) {
    return '-';
  }
  return formatDateTime(value, DateFormatStringEnum.DDMMYYYY);
};

const formatNumberValue = (value: number) => {
  if (value == null || Number.isNaN(value)) {
    return '-';
  }

  return `£ ${formatNumber(value, 2)}`;
};

export default function AvmResultItems({ avmResult }: { avmResult: AvmResult }) {
  const {
    inputs, outcome, valuationDate, type,
  } = avmResult ?? {};

  const avmSearchFailed = !outcome?.valuerName;

  return (
    <Collapsible
      id="task-details"
      level={2}
      headerCss={AvmResultHeaderCss}
      bodyContentCss={bodyContentCss}
      bodyCss={bodyCss}
      title={(
        <AvmResultHeader>
          <AvmResultType>{AvmResultTypeEnum[type]}</AvmResultType>
          <Seperator />
          <AvmResultDate>
            Date:
            <span>
              {formatDateValue(valuationDate)}
            </span>
          </AvmResultDate>
        </AvmResultHeader>
      )}
    >
      <AvmResultContainer>
        {
          avmSearchFailed ? (<NoResults>No results returned</NoResults>) : (
            <>
              <DataColumn>
                <OutputHeader>Request Data</OutputHeader>
                <Output label="Effective Date:" value={formatDateValue(inputs.effectiveDate)} />
                <Output label="Property Type:" value={PropertyType[outcome.propertyType] || '-'} />
                <Output label="Construction Type:" value={inputs.isStandardConstruction ? 'Standard' : 'Non Standard'} />
                <Output label="Ex-Council Property:" value={inputs.isExCouncil ? 'Yes' : 'No'} />
                <Output label="Number of Bedrooms" value={inputs.bedrooms || '-'} />
                <Output label="Number of Living Rooms:" value={inputs.livingRooms || '-'} />
                <Output label="Parking:" value={inputs.parking ? 'Yes' : 'No'} />
              </DataColumn>
              <DataColumn>
                <OutputHeader>Response Data</OutputHeader>
                <Output label="Valuer:" value={outcome.valuerName || '-'} />
                <Output label="Valuer Reference:" value={outcome.valuerReferenceNumber || '-'} />
                <Output label="Valuation:" value={formatNumberValue(outcome.valuationAmount)} />
                <Output label="Confidence:" value={type === 'InitialSearch' ? ConfidenceBandType[outcome.valuationConfidenceBand] : outcome.valuationConfidenceValue} />
                <Output label="Property Type:" value={PropertyType[outcome.propertyType] || '-'} />
                <Output label="Construction Type:" value={outcome.isStandardConstruction ? 'Standard' : 'Non Standard'} />
                <Output label="Ex-Council Property:" value={outcome.isExCouncil ? 'Yes' : 'No'} />
                <Output label="Number of Bedrooms" value={outcome.bedrooms || '-'} />
                <Output label="Number of Living Rooms:" value={outcome.livingRooms || '-'} />
                <Output label="Parking:" value={outcome.parking ? 'Yes' : 'No'} />
              </DataColumn>
            </>
          )
        }
      </AvmResultContainer>
    </Collapsible>
  );
}
